/**
 * Camera points of view.
 *
 * Each entry contributes two fragments to the final prompt: a `camera` line
 * (lens, height, framing) and a `pose` line (what the body is doing). They are
 * written to be composable — nothing here names a garment or a model, so any
 * POV works with any outfit.
 */
export const CAMERA_POVS = [
    /* ---------------------------- full body ---------------------------- */
    {
        id: 'full-front',
        name: 'Full body — front',
        group: 'full-body',
        description: 'The e-commerce hero. Straight-on, whole silhouette, no crop.',
        camera: 'eye-level, full-body framing, straight-on composition, 85mm lens, sharp focus, professional fashion photography',
        pose: 'relaxed stance with weight shifted slightly to one leg, one hand resting near the front pocket, the other hanging naturally by the side. Neutral facial expression, calm confidence, head slightly tilted, looking directly at camera',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'full-three-quarter',
        name: 'Full body — 3/4 turn',
        group: 'full-body',
        description: 'Body angled ~45°. Shows drape and side seams.',
        camera: 'eye-level, full-body framing, subject turned three-quarters to camera, 85mm lens, sharp focus, professional fashion photography',
        pose: 'body angled forty-five degrees away from the lens with the front shoulder dropped, weight on the back leg, front knee softly bent, chin turned back toward the camera with a composed, neutral expression',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'full-profile',
        name: 'Full body — side profile',
        group: 'full-body',
        description: 'Pure side view for silhouette and length.',
        garmentView: 'side',
        camera: 'eye-level, full-body framing, strict side profile, 85mm lens, sharp focus, professional fashion photography',
        pose: 'standing in clean side profile with the spine long, arms relaxed at the sides, gaze directed forward past the frame edge, weight evenly distributed',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'full-back',
        name: 'Full body — back',
        group: 'full-body',
        description: 'Rear construction: yokes, vents, back pockets, hem drop.',
        garmentView: 'back',
        camera: 'eye-level, full-body framing, photographed from directly behind the subject, 85mm lens, sharp focus, professional fashion photography',
        pose: 'standing with the back squarely to the camera, arms relaxed at the sides, head facing forward away from the lens, shoulders level',
        preferredRatio: '2:3',
        isCrop: false,
    },
    /* ------------------------- portrait / crops ------------------------ */
    {
        id: 'three-quarter-crop',
        name: 'Waist-up',
        group: 'portrait',
        description: 'Mid-thigh to head. The catalogue workhorse.',
        camera: 'eye-level, waist-up framing, straight-on composition, 85mm lens, shallow depth of field, sharp focus on the garment',
        pose: 'squared to camera with one hand lightly touching the opposite forearm, shoulders relaxed and open, steady neutral gaze into the lens',
        preferredRatio: '4:5',
        isCrop: true,
    },
    {
        id: 'bust-portrait',
        name: 'Chest-up portrait',
        group: 'portrait',
        description: 'Neckline, collar, earrings, makeup.',
        camera: 'eye-level, chest-up portrait framing, 105mm lens, shallow depth of field, sharp focus on the eyes and neckline',
        pose: 'head and shoulders squared to the lens with the chin slightly lowered, a soft neutral expression, eyes directly on camera',
        preferredRatio: '4:5',
        isCrop: true,
    },
    {
        id: 'seated',
        name: 'Seated',
        group: 'portrait',
        description: 'Seated on a stool — shows how the fabric folds sitting.',
        camera: 'eye-level, three-quarter framing, subject seated on a simple studio stool, 85mm lens, sharp focus',
        pose: 'seated upright on a plain studio stool with one ankle crossed over the opposite knee, forearms resting easily on the thighs, back straight, looking directly at the camera',
        preferredRatio: '4:5',
        isCrop: true,
    },
    /* ----------------------------- details ----------------------------- */
    {
        id: 'detail-fabric',
        name: 'Fabric detail',
        group: 'detail',
        description: 'Macro on weave, sequins, embroidery, texture.',
        garmentView: 'detail',
        camera: 'macro detail shot, 100mm macro lens, extremely shallow depth of field, raking light across the surface to reveal weave and thread, tack-sharp focus on the fabric',
        pose: 'tight crop on the garment surface across the torso, the body held still and slightly angled so the material catches the light',
        preferredRatio: '1:1',
        isCrop: true,
        lead: 'macro close-up photograph of the fabric surface of the garment — the frame filled edge to edge by weave, embroidery and texture, no face and no full figure in frame',
    },
    {
        id: 'detail-neckline',
        name: 'Neckline & shoulder',
        group: 'detail',
        description: 'Collar, armhole finish, shoulder seam, layering.',
        garmentView: 'detail',
        camera: 'close detail framing on the neckline and shoulder, 85mm lens, shallow depth of field, sharp focus on the seam and edge finish',
        pose: 'upper chest and shoulder filling the frame, head turned away and cropped at the jaw, torso angled to reveal the neckline construction and any layering underneath',
        preferredRatio: '1:1',
        isCrop: true,
        lead: 'tight close-up photograph of the neckline, collar and shoulder seam of the garment — the frame filled by upper chest and shoulder, the head cropped out at the jaw',
    },
    {
        id: 'detail-hem',
        name: 'Hem & footwear',
        group: 'detail',
        description: 'Trouser break, hem finish, shoes.',
        garmentView: 'detail',
        camera: 'low framing on the lower legs and hem, 50mm lens, sharp focus on the hem line and the floor contact',
        pose: 'legs from mid-thigh down, weight on one leg with the other slightly forward so the hem breaks naturally over the foot',
        preferredRatio: '1:1',
        isCrop: true,
        lead: 'low close-up photograph of the lower legs, trouser hem and shoes — the frame filled from mid-thigh down, nothing above the waist in frame',
    },
    /* ----------------------------- dynamic ----------------------------- */
    {
        id: 'walking',
        name: 'Walking / mid-stride',
        group: 'dynamic',
        description: 'Movement — how the garment behaves in motion.',
        camera: 'eye-level, full-body framing, subject walking toward the camera, 85mm lens, fast shutter freezing the motion, sharp focus',
        pose: 'caught mid-stride walking toward the lens, one leg forward and the fabric swinging with the movement, arms loose and naturally counter-swinging, relaxed expression looking just past the camera',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'over-shoulder',
        name: 'Over the shoulder',
        group: 'dynamic',
        description: 'Back turned, glancing back. Editorial staple.',
        garmentView: 'back',
        camera: 'eye-level, three-quarter framing from behind, 85mm lens, sharp focus on the face and the back of the garment',
        pose: 'back turned to the camera with the head rotated to look back over one shoulder, chin near the shoulder line, hands relaxed, a quiet direct gaze into the lens',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'candid-motion',
        name: 'Candid turn',
        group: 'dynamic',
        description: 'Loose, unposed energy without leaving the studio.',
        camera: 'eye-level, full-body framing, 50mm lens, candid documentary feel while remaining a controlled studio shot, sharp focus',
        pose: 'caught mid-turn with the hair moving, one hand pushing through the hair and the other loose at the side, an unguarded natural expression looking off-camera',
        preferredRatio: '2:3',
        isCrop: false,
    },
    /* ------------------------------ angles ----------------------------- */
    {
        id: 'low-angle',
        name: 'Low angle (hero)',
        group: 'angle',
        description: 'Camera below eye line. Lengthens the leg, adds authority.',
        camera: 'low camera angle positioned below the waist looking slightly upward, full-body framing, 50mm lens, sharp focus, elongated proportions',
        pose: 'standing tall with the shoulders squared and the chin level, one hand on the hip, weight planted evenly, a confident direct gaze down into the lens',
        preferredRatio: '2:3',
        isCrop: false,
    },
    {
        id: 'high-angle',
        name: 'High angle',
        group: 'angle',
        description: 'Slightly above eye line, looking up at camera.',
        camera: 'camera raised slightly above head height angled downward, three-quarter framing, 50mm lens, sharp focus',
        pose: 'looking up toward the raised camera with the chin lifted, arms relaxed and slightly away from the body, shoulders open',
        preferredRatio: '4:5',
        isCrop: true,
    },
];
export const POV_GROUP_LABELS = {
    'full-body': 'Full body',
    portrait: 'Portrait & crops',
    detail: 'Details',
    dynamic: 'Movement',
    angle: 'Camera angles',
};
/**
 * Poses — what the body is doing, independent of where the camera is.
 *
 * Splitting these from the camera is what lets one set-up return a standing
 * frame, a walking frame and a reclining frame instead of the same position
 * three times. `posture` matters: the scene clause is built from it, so a
 * reclining pose reads "reclines in the long grass" rather than "stands
 * against" it.
 */
export const POSE_PRESETS = [
    /* ------------------------------ static ------------------------------- */
    {
        id: 'weight-shift',
        name: 'Weight shift',
        group: 'static',
        posture: 'standing',
        description: 'The catalogue default. Relaxed contrapposto.',
        prompt: 'relaxed stance with the weight shifted onto one leg, one hand resting near the front pocket, the other hanging naturally by the side, head slightly tilted, a calm neutral expression looking directly at camera',
    },
    {
        id: 'hands-pockets',
        name: 'Hands in pockets',
        group: 'static',
        posture: 'standing',
        description: 'Both hands buried. Reads easy and unposed.',
        prompt: 'standing square with both hands pushed into the pockets, shoulders dropped and relaxed, chin level, looking straight down the lens',
    },
    {
        id: 'arms-relaxed',
        name: 'Arms down',
        group: 'static',
        posture: 'standing',
        description: 'Nothing in the way of the garment.',
        prompt: 'standing tall and still with both arms hanging loosely at the sides, palms turned slightly inward, feet a little apart, a level neutral gaze',
    },
    {
        id: 'hand-hip',
        name: 'Hand on hip',
        group: 'static',
        posture: 'standing',
        description: 'Opens the silhouette, defines the waist.',
        prompt: 'one hand set on the hip with the elbow angled away from the body, the other arm relaxed, weight on the opposite leg, a composed confident expression',
    },
    {
        id: 'arms-crossed',
        name: 'Arms folded',
        group: 'static',
        posture: 'standing',
        description: 'Closed and self-contained.',
        prompt: 'arms folded loosely across the middle, shoulders square, weight even, a steady unsmiling look into the lens',
    },
    {
        id: 'holding-bag',
        name: 'Holding a bag',
        group: 'static',
        posture: 'standing',
        description: 'For shoots where the bag is the product.',
        prompt: 'holding a bag against the front of the body with both hands, forearms relaxed, body angled a few degrees off the lens, looking away to the side past the camera',
    },
    {
        id: 'adjusting-sleeve',
        name: 'Adjusting a sleeve',
        group: 'static',
        posture: 'standing',
        description: 'Hands drawn to a detail. Reads unposed.',
        prompt: 'head lowered slightly, both hands drawn to one cuff as if turning back a sleeve, eyes down on the hands, an unguarded private expression',
    },
    /* ----------------------------- movement ------------------------------ */
    {
        id: 'mid-stride',
        name: 'Mid-stride',
        group: 'movement',
        posture: 'walking',
        description: 'Walking toward the lens. Shows how fabric moves.',
        prompt: 'caught mid-stride walking toward the camera, one leg forward and the fabric swinging with the movement, arms loose and counter-swinging, a relaxed expression looking just past the lens',
    },
    {
        id: 'carrying-weight',
        name: 'Walking, bag in hand',
        group: 'movement',
        posture: 'walking',
        description: 'Mid-stride with a heavy bag pulling one shoulder down.',
        prompt: 'mid-stride in strict side profile, one arm hanging straight down carrying a large soft bag low by its handles so its weight pulls that shoulder down and forward, the near leg extended and heel just landing, the other trailing back, chin level and gaze fixed ahead out of frame',
    },
    {
        id: 'hood-up',
        name: 'Hood up',
        group: 'static',
        posture: 'standing',
        description: 'Hood pulled over the head, framing the face.',
        prompt: 'standing square with the knitted hood pulled up over the head so it frames the face closely, one hand in a trouser pocket and the other holding a bag against the body, shoulders relaxed and chin slightly lifted',
    },
    {
        id: 'wind-in-hair',
        name: 'Wind across the face',
        group: 'gesture',
        posture: 'standing',
        description: 'Loose hair blown across the face. Needs an exposed location.',
        prompt: 'long loose hair caught by a strong crosswind and blown across the face and out behind her, a few strands falling over the cheek and mouth, head turned to look away past the camera with lips slightly parted, making no attempt to hold the hair back',
    },
    {
        id: 'walk-away-glance',
        name: 'Walking away, glancing back',
        group: 'movement',
        posture: 'walking',
        description: 'Shows the back of a garment in motion.',
        prompt: 'walking away from the camera with the head turned back over one shoulder, hair swinging with the turn, one arm mid-swing, a direct look back into the lens',
    },
    {
        id: 'turning-arms-out',
        name: 'Turning, arms out',
        group: 'movement',
        posture: 'walking',
        description: 'Big, kinetic, campaign energy.',
        prompt: 'caught mid-turn with both arms extended out and away from the body, hands loose, hair and fabric lifting with the movement, head down and turned to the side, eyes off camera',
    },
    {
        id: 'hair-sweep',
        name: 'Hand through hair',
        group: 'movement',
        posture: 'standing',
        description: 'Wind in the hair. Softens a static frame.',
        prompt: 'one hand pushing back through the hair with strands lifting across the face in the wind, the other arm loose at the side, chin raised slightly, eyes half-closed against the light',
    },
    {
        id: 'coat-open',
        name: 'Holding the coat open',
        group: 'movement',
        posture: 'walking',
        description: 'Shows a lining and the layers underneath.',
        prompt: 'mid-step with one hand holding the front edge of the outer layer open and away from the body, revealing what is worn underneath, the other arm swinging back, looking ahead past the camera',
    },
    /* ------------------------------ ground ------------------------------- */
    {
        id: 'reclining-elbow',
        name: 'Reclining on one elbow',
        group: 'ground',
        posture: 'reclining',
        description: 'Editorial staple. Needs grass, sand or a floor.',
        prompt: 'reclining on one hip propped up on a forearm, legs extended and crossed at the ankle with the skirt or trouser fabric falling in long folds, the free arm raised with the hand resting behind the head, chin tilted toward the camera in a calm direct gaze',
        needsSupport: true,
        onGroundSurface: true,
    },
    {
        id: 'seated-leaning-back',
        name: 'Seated, leaning back on a hand',
        group: 'ground',
        posture: 'seated',
        description: 'Sitting in grass or sand, weight back on one arm.',
        prompt: 'sitting low in the tall grass with the weight taken back on one straight arm behind her, one knee drawn up and the other leg extended, the free hand resting on the raised knee, torso open to the camera and head turned to look off to the side',
        needsSupport: true,
        onGroundSurface: true,
    },
    {
        id: 'lying-back',
        name: 'Lying back',
        group: 'ground',
        posture: 'reclining',
        description: 'Shot from above. Fabric spreads flat.',
        prompt: 'lying back with the shoulders down and one knee drawn up, both arms relaxed above the head, hair fanned out, eyes looking straight up into the lens',
        needsSupport: true,
        onGroundSurface: true,
    },
    {
        id: 'seated-ground',
        name: 'Sitting on the ground',
        group: 'ground',
        posture: 'seated',
        description: 'Knees up, forearms resting. Casual.',
        prompt: 'sitting on the ground with both knees drawn up and the forearms resting across them, hands loose, back long, head turned slightly to look into the lens',
        needsSupport: true,
        onGroundSurface: true,
    },
    {
        id: 'crouching',
        name: 'Crouched low',
        group: 'ground',
        posture: 'crouching',
        description: 'Compact. Good for outerwear and boots.',
        prompt: 'crouched down on the balls of the feet with the forearms resting on the knees and the hands hanging loose between them, back straight, looking level into the camera',
        onGroundSurface: true,
    },
    {
        id: 'seated-stool',
        name: 'Seated on a stool',
        group: 'ground',
        posture: 'seated',
        description: 'The studio sit. Shows how fabric folds seated.',
        prompt: 'seated upright on a plain studio stool with one ankle crossed over the opposite knee, forearms resting easily on the thighs, back straight, looking directly at the camera',
        needsSupport: true,
    },
    {
        id: 'seated-steps',
        name: 'Seated on steps',
        group: 'ground',
        posture: 'seated',
        description: 'One leg extended, one drawn in.',
        prompt: 'sitting on a low step with one leg extended down and the other drawn in, leaning back on one straight arm, the free hand resting on the knee, chin lifted toward the lens',
        needsSupport: true,
    },
    /* ------------------------------- prop -------------------------------- */
    {
        id: 'leaning-fence',
        name: 'Leaning on a rail',
        group: 'prop',
        posture: 'leaning',
        description: 'Forearm on a fence. Relaxed rural editorial.',
        prompt: 'leaning with one forearm folded along the top of a weathered wooden rail, the body angled into it and the weight carried on the near leg, head turned away from the camera looking off into the distance',
        needsSupport: true,
    },
    {
        id: 'leaning-wall',
        name: 'Shoulder to a wall',
        group: 'prop',
        posture: 'leaning',
        description: 'One shoulder planted, ankles crossed.',
        prompt: 'standing with one shoulder set against a wall and the ankles loosely crossed, one hand in a pocket, the head tipped back against the surface, looking sideways into the lens',
        needsSupport: true,
    },
    {
        id: 'hand-on-rock',
        name: 'Hand on a rock',
        group: 'prop',
        posture: 'standing',
        description: 'Anchors the model into an outdoor scene.',
        prompt: 'standing beside a large pale boulder with one hand resting flat on its surface, the weight settled onto the far leg, body half-turned toward the camera with a steady level gaze',
        needsSupport: true,
    },
    /* ------------------------------ gesture ------------------------------ */
    {
        id: 'looking-away',
        name: 'Looking off camera',
        group: 'gesture',
        posture: 'standing',
        description: 'Editorial distance. Nothing sold to the lens.',
        prompt: 'body squared to the camera but the head turned well away to the side, eyes fixed on something out of frame, lips relaxed and closed, a still and slightly distant expression',
    },
    {
        id: 'over-shoulder-glance',
        name: 'Over the shoulder',
        group: 'gesture',
        posture: 'standing',
        description: 'Back to camera, face returning to it.',
        prompt: 'back turned to the camera with the head rotated to look back over one shoulder, chin close to the shoulder line, hands relaxed at the sides, a quiet direct gaze into the lens',
    },
    {
        id: 'hand-to-jaw',
        name: 'Hand near the jaw',
        group: 'gesture',
        posture: 'standing',
        description: 'Brings a hand into a portrait crop.',
        prompt: 'one hand raised so the fingertips rest lightly along the jaw, elbow tucked in, the head tilted a few degrees into that hand, eyes soft and level on the lens',
    },
    /* ------------------------------ dynamic ------------------------------ */
    /*
     * Movement with real energy in it. The earlier movement poses are a walk
     * and a turn; these are the frames a campaign is cut from — a jump held
     * at its top, a spin caught mid-blur, a run — and they are asked for as a
     * single frozen instant so the renderer does not smear the garment.
     */
    {
        id: 'jump-frozen',
        name: 'Jump, frozen',
        group: 'movement',
        posture: 'standing',
        description: 'Both feet off the ground at the top of a jump.',
        prompt: 'caught at the very top of a jump with both feet clear of the ground, knees slightly bent, arms lifted out for balance, hair and fabric suspended in the air, a bright open expression, frozen razor-sharp with no motion blur',
    },
    {
        id: 'spin-turn',
        name: 'Spin',
        group: 'movement',
        posture: 'standing',
        description: 'A full turn with the skirt or coat flaring out.',
        prompt: 'mid-spin on the ball of one foot, the body turning through the frame, the hem and any loose fabric flaring out with the rotation, arms lifted slightly, face turned to the lens at the instant of passing it',
    },
    {
        id: 'running',
        name: 'Running',
        group: 'movement',
        posture: 'walking',
        description: 'A real run across the frame, not a jog in place.',
        prompt: 'running across the frame at full stride, one foot planted and the other kicked back, arms pumping, torso leaning forward, hair streaming back, an exhilarated expression, the whole body frozen sharp',
    },
    {
        id: 'stride-stairs',
        name: 'Up the steps',
        group: 'movement',
        posture: 'walking',
        description: 'Climbing steps two at a time, looking back.',
        prompt: 'climbing a flight of steps with a long stride, one hand skimming the rail, the body turned to glance back over the shoulder at the camera below',
    },
    {
        id: 'dance-step',
        name: 'Dance step',
        group: 'movement',
        posture: 'standing',
        description: 'A loose, unchoreographed step to music nobody hears.',
        prompt: 'caught in a loose dance step, weight rocking onto one foot, hips turned, one arm swung across the body and the other raised, eyes half closed and a wide unguarded smile',
    },
    {
        id: 'kick-back',
        name: 'Heel kick',
        group: 'movement',
        posture: 'standing',
        description: 'One heel kicked up behind, playful.',
        prompt: 'standing on one leg with the other heel kicked up behind, the body leaning a little forward to balance, one hand on the hip and the other reaching out, a playful laughing expression',
    },
    /* ----------------------------- lifestyle ----------------------------- */
    /*
     * Someone living in the clothes rather than presenting them. Each of these
     * carries a small piece of the world — a cup, a phone, a doorway — because
     * a lifestyle frame is the prop as much as the pose, and a pose with
     * nothing in the hands reads as a studio pose that was moved outdoors.
     */
    {
        id: 'cafe-table',
        name: 'At a café table',
        group: 'lifestyle',
        posture: 'seated',
        description: 'Seated at a small round table with a coffee.',
        prompt: 'seated at a small round café table, one elbow on the tabletop and a small espresso cup held loosely near the mouth, legs crossed at the knee, the body angled to the camera, gaze drifting off to the street with a faint smile',
        needsSupport: true,
    },
    {
        id: 'coffee-walk',
        name: 'Walking with a coffee',
        group: 'lifestyle',
        posture: 'walking',
        description: 'Mid-stride, takeaway cup in hand.',
        prompt: 'walking towards the camera at an easy pace with a takeaway coffee cup held in one hand at chest height, the other hand in a pocket, a relaxed contented expression, looking just past the lens',
    },
    {
        id: 'crossing-street',
        name: 'Crossing the street',
        group: 'lifestyle',
        posture: 'walking',
        description: 'Halfway across the crossing, glancing sideways.',
        prompt: 'halfway across a pedestrian crossing at a brisk stride, head turned to glance down the street, one arm swinging and the other holding a bag strap at the shoulder',
    },
    {
        id: 'phone-call',
        name: 'On the phone',
        group: 'lifestyle',
        posture: 'standing',
        description: 'A phone held to the ear, mid-conversation.',
        prompt: 'standing with a phone held to the ear, the other arm folded loosely across the middle, weight on one leg, laughing softly at something being said, eyes away from the camera',
    },
    {
        id: 'leaning-doorway',
        name: 'In a doorway',
        group: 'lifestyle',
        posture: 'leaning',
        description: 'Shoulder against the door frame, arms crossed.',
        prompt: 'leaning one shoulder against the frame of a doorway, ankles crossed, arms folded loosely, head tilted with a calm half-smile aimed at the camera',
        needsSupport: true,
    },
    {
        id: 'stoop-seated',
        name: 'On the front step',
        group: 'lifestyle',
        posture: 'seated',
        description: 'Sat on a doorstep, elbows on the knees.',
        prompt: 'seated on a stone doorstep with the knees apart and forearms resting on them, hands loosely clasped, leaning forward slightly, an easy direct look into the lens',
        needsSupport: true,
        onGroundSurface: false,
    },
    {
        id: 'bar-counter',
        name: 'At the bar',
        group: 'lifestyle',
        posture: 'leaning',
        description: 'One elbow on the counter, a glass in hand.',
        prompt: 'leaning sideways against a bar counter with one elbow resting on it and a glass held loosely in that hand, the other hand in a pocket, ankles crossed, turned to the camera with an amused expression',
        needsSupport: true,
    },
    {
        id: 'bench-reading',
        name: 'Reading on a bench',
        group: 'lifestyle',
        posture: 'seated',
        description: 'Absorbed in a paperback, legs crossed.',
        prompt: 'seated on a bench with legs crossed, an open paperback held in one hand and the other arm laid along the backrest, head bowed slightly to the page, unaware of the camera',
        needsSupport: true,
    },
    {
        id: 'bike-lean',
        name: 'With a bicycle',
        group: 'lifestyle',
        posture: 'standing',
        description: 'Standing astride a city bike, one foot down.',
        prompt: 'standing astride a classic city bicycle with one foot on the ground and both hands resting on the handlebars, the body upright and relaxed, looking at the camera with a small smile',
    },
    {
        id: 'hailing-taxi',
        name: 'Hailing a taxi',
        group: 'lifestyle',
        posture: 'standing',
        description: 'One arm raised to the road, bag on the shoulder.',
        prompt: 'standing at the kerb with one arm raised high to hail a taxi, the body stretched slightly with the reach, a bag strap on the opposite shoulder, looking down the road rather than at the camera',
    },
    {
        id: 'window-shopping',
        name: 'Window shopping',
        group: 'lifestyle',
        posture: 'standing',
        description: 'Paused at a shop window, seen in three-quarter.',
        prompt: 'paused in front of a shop window, the body in three-quarter to the camera, one hand raised to touch the glass and the head turned to look in, the reflection faint in the pane',
    },
    {
        id: 'laughing-off-frame',
        name: 'Laughing off frame',
        group: 'lifestyle',
        posture: 'standing',
        description: 'Mid-laugh at someone just out of shot.',
        prompt: 'turned a little away from the camera and laughing openly at someone just out of frame, one hand lifted towards them mid-gesture, shoulders loose, eyes creased with the laugh',
    },
    {
        id: 'stride-pocket',
        name: 'Stride, hand in pocket',
        group: 'lifestyle',
        posture: 'walking',
        description: 'Walking past the camera, one hand in a trouser pocket.',
        prompt: 'walking past the camera at an easy stride, one hand pushed into a front trouser pocket and the other arm swinging loosely, the body turned a few degrees from the lens, chin level, a cool composed look through the lens as she passes',
    },
    {
        id: 'kerb-bag',
        name: 'At the kerb with a bag',
        group: 'lifestyle',
        posture: 'standing',
        description: 'Waiting to cross, a small bag held low by its handles.',
        prompt: 'standing square at the kerb with the weight on one leg, a small structured bag held low in front of the body by its handles with both hands, the head turned to look down the street away from the camera, unhurried, waiting to cross',
    },
    {
        id: 'cafe-menu',
        name: 'Reading the menu',
        group: 'lifestyle',
        posture: 'seated',
        description: 'Seated at a café table, the menu open, glancing over to answer someone.',
        prompt: 'seated at a café table with a menu held open in one hand, forearms resting on the table, the head turned to glance over the shoulder as if answering someone beside the table, mid-sentence, caught rather than posed',
        needsSupport: true,
    },
    /* ----------------------------- influencer ---------------------------- */
    /*
     * The way an Instagram account poses rather than the way a catalogue does:
     * looser, more knowing, a little playful, the body in motion and the
     * camera treated as a friend. These are the poses American street-style
     * and influencer shoots repeat because they flatter and they read at the
     * size of a phone screen.
     */
    {
        id: 'cross-step',
        name: 'Crossover step',
        group: 'influencer',
        posture: 'walking',
        description: 'One foot crossing in front of the other, hips angled.',
        prompt: 'caught mid-step walking towards the camera with one foot crossing in front of the other, hips angled and shoulders relaxed, one arm swinging and the other hand brushing the hair, a confident easy smile',
    },
    {
        id: 'hair-tuck',
        name: 'Hair behind the ear',
        group: 'influencer',
        posture: 'standing',
        description: 'Tucking a strand behind the ear, glancing up.',
        prompt: 'tucking a strand of hair behind one ear with the fingertips, the head tilted down a little and the eyes glancing up at the camera, a soft unforced smile, weight on one leg',
    },
    {
        id: 'sunglasses-lower',
        name: 'Over the sunglasses',
        group: 'influencer',
        posture: 'standing',
        description: 'Lowering the sunglasses to look over them.',
        prompt: 'lowering a pair of sunglasses down the nose with one finger to look over the top of them at the camera, chin dipped, a knowing half-smile, the other hand in a pocket',
    },
    {
        id: 'look-back-smile',
        name: 'Looking back, smiling',
        group: 'influencer',
        posture: 'walking',
        description: 'Walking away and turning to smile over the shoulder.',
        prompt: 'walking away from the camera and turning to look back over one shoulder with a bright natural smile, hair swinging with the turn, one hand trailing loosely behind',
    },
    {
        id: 'walk-look-down',
        name: 'Walking, eyes down',
        group: 'influencer',
        posture: 'walking',
        description: 'Mid-stride towards camera, looking down at the pavement.',
        prompt: 'walking towards the camera at an easy pace and looking down at the pavement just ahead, one hand running through the hair, a hint of a smile, the moment between two steps',
    },
    {
        id: 'collar-hold',
        name: 'Holding the collar',
        group: 'influencer',
        posture: 'standing',
        description: 'Both hands at the lapels of the jacket.',
        prompt: 'standing with both hands lightly holding the lapels or collar of the jacket at chest height, shoulders back, the head turned a few degrees and eyes on the camera, a composed confident look',
    },
    {
        id: 'wall-knee',
        name: 'Against the wall, knee up',
        group: 'influencer',
        posture: 'leaning',
        description: 'Back to the wall, one foot flat against it.',
        prompt: 'leaning back against a wall with one knee bent and that foot set flat against the wall behind, hands resting on the bent thigh, head tipped back slightly, a relaxed sideways look at the camera',
        needsSupport: true,
    },
    {
        id: 'laugh-hand',
        name: 'Laughing behind a hand',
        group: 'influencer',
        posture: 'standing',
        description: 'A real laugh, half hidden behind the fingers.',
        prompt: 'caught laughing, one hand lifted to half cover the mouth, eyes creased and looking just off camera, the shoulders lifted with the laugh, the other arm relaxed',
    },
    {
        id: 'steps-crossed',
        name: 'On the steps, legs crossed',
        group: 'influencer',
        posture: 'seated',
        description: 'Sitting on steps, legs crossed, elbow on the knee.',
        prompt: 'seated on a step with the legs crossed at the knee, one elbow resting on the upper knee and the chin near the hand, the body angled to the camera, an easy direct look and a small smile',
        needsSupport: true,
    },
    /*
     * From the photographer's posing list: what to do with the hands, how to
     * add height and depth, and — for editorial — the bold, the hidden and the
     * shadowed. Each is written so the arms do different things: two hands
     * doing the same thing is the stiffest picture there is.
     */
    {
        id: 'hat-brim',
        name: 'Hand on the hat brim',
        group: 'gesture',
        posture: 'standing',
        description: 'A wide-brimmed hat, one hand on the brim. Gives the hands a job.',
        prompt: 'standing with the weight on one leg, one hand raised to hold the brim of a wide-brimmed hat she is wearing, the other arm relaxed at her side, the face turned three-quarters to the camera and the eyes on the lens from under the brim',
    },
    {
        id: 'hand-on-face',
        name: 'Hand on the face',
        group: 'gesture',
        posture: 'standing',
        description: 'Fingertips along the cheekbone, the other arm down. Asymmetric.',
        prompt: 'one hand raised to the face with the fingertips resting lightly along the cheekbone and jaw, the other arm relaxed down by her side so the two arms do different things, the head tilted slightly, a calm look into the lens',
    },
    {
        id: 'hands-on-hips',
        name: 'Hands on hips',
        group: 'static',
        posture: 'standing',
        description: 'Both hands on the hips, elbows out. Attitude.',
        prompt: 'standing with both hands on the hips and the elbows out to the sides, the weight shifted onto one leg so the hips sit at an angle, shoulders back, a self-assured look straight into the lens',
    },
    {
        id: 'hands-in-hair',
        name: 'Hands through the hair',
        group: 'gesture',
        posture: 'standing',
        description: 'Both hands running back through the hair. Natural, unposed.',
        prompt: 'both hands running back through the hair from the temples, the elbows lifted and open, the head tipped slightly back and the eyes half closed, an unposed in-the-moment feel',
    },
    {
        id: 'jacket-wrap',
        name: 'Wrapped up in the jacket',
        group: 'gesture',
        posture: 'standing',
        description: 'Pulls the jacket or cardigan tight around her. Playful.',
        prompt: 'wrapping the outer layer tightly around the body with both arms, the hands gripping its front edges, the shoulders lifted a little as if against the cold, a playful half smile at the camera',
    },
    {
        id: 'sit-forward',
        name: 'Seated, leaning forward',
        group: 'ground',
        posture: 'seated',
        description: 'On a low stool, forearms on the knees, face up to the lens.',
        prompt: 'seated on a low stool, leaning forward with the forearms resting on the knees and the hands loosely clasped, the feet flat and apart, the face lifted towards the lens',
        needsSupport: true,
    },
    {
        id: 'wall-shoulder',
        name: 'Shoulder to the wall',
        group: 'prop',
        posture: 'leaning',
        description: 'One shoulder against the wall, ankles crossed, looking back.',
        prompt: 'leaning one shoulder against the wall with the body angled along it, the ankles crossed, one hand in a pocket and the other relaxed, looking back to the camera over the near shoulder',
        needsSupport: true,
    },
    {
        id: 'knee-pop',
        name: 'Knee bent forward',
        group: 'static',
        posture: 'standing',
        description: 'Weight back, front knee bent in, toes down. Adds depth.',
        prompt: 'the weight on the back leg, the front knee bent and turned slightly inward with only the toes of that foot touching the ground, the hips angled, one hand resting lightly at the thigh — a classic fashion stance that gives the silhouette depth',
    },
    {
        id: 'leg-cross-stand',
        name: 'Standing, legs crossed',
        group: 'static',
        posture: 'standing',
        description: 'One leg crossed in front of the other, hips turned away.',
        prompt: 'standing with one leg crossed in front of the other at the shins, the toes of the front foot pointed, the hips angled away and the shoulders turned back towards the camera',
    },
    {
        id: 'product-present',
        name: 'Showing the bag',
        group: 'gesture',
        posture: 'standing',
        description: 'Holds the bag up by the shoulder, towards the camera. Commercial.',
        prompt: 'holding the bag up near the shoulder with one hand and turning it a little towards the camera to show it, the other hand relaxed, a warm natural smile and the eyes on the lens',
    },
    {
        id: 'stare-down',
        name: 'The stare-down',
        group: 'editorial',
        posture: 'standing',
        description: 'Square to camera, chin down, intense and still.',
        prompt: 'standing square to the camera with the chin lowered a fraction, a steady, intense, unsmiling gaze straight into the lens, the shoulders still and the arms quiet at the sides',
    },
    {
        id: 'eyes-closed',
        name: 'Eyes closed',
        group: 'editorial',
        posture: 'standing',
        description: 'Eyes softly closed, face tipped up. Quiet, introspective.',
        prompt: 'the eyes softly closed and the face tipped slightly upward as if towards the light, the lips relaxed, one hand resting at the collarbone, a still and introspective mood',
    },
    {
        id: 'hand-on-head',
        name: 'Hand on the head, turned away',
        group: 'editorial',
        posture: 'standing',
        description: 'Hand resting on the head, eyes closed, turning away. Mysterious.',
        prompt: 'turned slightly away from the camera, one hand resting on the top of the head with the elbow raised, the eyes closed and the other arm relaxed, an enigmatic, quiet mood',
    },
    {
        id: 'angular-editorial',
        name: 'Angular high-fashion',
        group: 'editorial',
        posture: 'standing',
        description: 'Sharp angles: elbow up, knee in, torso twisted. Bold.',
        prompt: 'a bold high-fashion pose built from sharp angles: one elbow raised high with the hand behind the head, the other hand pressed to the hip, one knee bent and turned in, the torso twisted against the hips, a cool unsmiling expression',
    },
    {
        id: 'shadow-turn',
        name: 'Half in shadow',
        group: 'editorial',
        posture: 'standing',
        description: 'Turned into the light so half the face falls in shadow.',
        prompt: 'the face turned into the light so a band of shadow falls across half of it, one eye in light and one in shade, the shoulders angled away, a still, intriguing expression',
    },
];
export const POSE_GROUP_LABELS = {
    static: 'Standing',
    movement: 'Movement',
    ground: 'Ground & seated',
    prop: 'Against something',
    gesture: 'Gesture & gaze',
    lifestyle: 'Lifestyle',
    influencer: 'Influencer',
    editorial: 'Editorial',
};
export function findPose(id) {
    return POSE_PRESETS.find((p) => p.id === id);
}
export const LIGHTING_PRESETS = [
    /*
     * The place's own light, for a place given as a photograph or in words: the
     * model is lit the way that place is, rather than by a softbox carried into
     * it. The default for a roll shot from pose photographs in a saved place.
     */
    {
        id: 'match-setting',
        name: 'The place’s own light',
        description: 'Lit the way the place is: its direction, colour and softness.',
        prompt: 'the setting’s own light — the same direction, colour, softness and time of day as the place itself, as if she were really photographed there, with shadows and reflections that agree with it',
    },
    {
        id: 'high-key-soft',
        name: 'High-key commercial',
        description: 'Soft, even, near shadowless. The default e-commerce look.',
        prompt: 'soft, even studio lighting with minimal shadow, high-key setup, clean commercial fashion look. No dramatic contrast',
    },
    {
        id: 'soft-box-natural',
        name: 'Soft box, natural falloff',
        description: 'One large source, gentle shadow on the shadow side.',
        prompt: 'a single large softbox placed slightly off-axis with a subtle natural falloff across the shadow side, gentle contrast, soft-edged shadows',
    },
    {
        id: 'editorial-contrast',
        name: 'Editorial contrast',
        description: 'Directional key, deeper shadows, sculpted.',
        prompt: 'directional key light with deliberate shadow shaping, moderate contrast, sculpted highlights along the cheekbone and the garment edges, editorial magazine lighting',
    },
    {
        id: 'hard-flash',
        name: 'Direct flash',
        description: 'On-camera flash, crisp shadow, 90s campaign energy.',
        prompt: 'direct on-camera flash with a crisp hard-edged shadow behind the subject, punchy saturated colour, contemporary campaign look',
    },
    {
        id: 'overcast-soft',
        name: 'Overcast',
        description: 'Heavy cloud. Flat, shadowless, cool. The location default.',
        prompt: 'flat even daylight under a heavy overcast sky, no visible shadows, a cool neutral cast and soft wraparound light on the face',
    },
    {
        id: 'broken-cloud',
        name: 'Break in the cloud',
        description: 'Hard low sun under a dark sky. Dramatic, and the hardest to fake.',
        prompt: 'low directional sunlight breaking through a gap in heavy cloud, lighting her cleanly from one side with a crisp defined shadow edge while the sky and landscape behind stay dark and stormy, a high-contrast shaft of light isolating the subject against the gloom',
    },
    {
        id: 'golden-hour',
        name: 'Golden hour',
        description: 'Low warm sun, long shadows, gentle flare.',
        prompt: 'low warm sunlight raking in from behind and to one side, long soft shadows across the ground, a gentle haze of flare at the frame edge and warm rim light along the hair and shoulders',
    },
    {
        id: 'coastal-grey',
        name: 'Coastal grey',
        description: 'Bright maritime haze. Cool and desaturated.',
        prompt: 'bright but diffuse maritime daylight under high cloud, a cool desaturated cast, soft even illumination with no hard shadow',
    },
    {
        id: 'desert-sun',
        name: 'High sun',
        description: 'Hard midday light, crisp shadows.',
        prompt: 'strong high sun with crisp defined shadows under the chin and jaw, a pale hazy sky, high ambient bounce from dry pale ground',
    },
    {
        id: 'blue-hour',
        name: 'Blue hour',
        description: 'Just after sunset. Cool, dim, no direct sun.',
        prompt: 'cool dim twilight shortly after sunset with no direct sun, soft ambient light from the whole sky, deep blue shadows and a quiet even exposure',
    },
    {
        id: 'window-daylight',
        name: 'Window daylight',
        description: 'Cool diffused daylight raking in from one side.',
        prompt: 'cool diffused daylight raking in from a large window to camera left, soft gradient across the backdrop, natural colour temperature',
    },
    {
        id: 'rim-backlight',
        name: 'Rim / backlight',
        description: 'Separation glow along the silhouette edge.',
        prompt: 'soft frontal fill combined with a bright rim light behind the subject that traces a glow along the silhouette edge, clean separation from the background',
    },
    {
        id: 'street-shade',
        name: 'City shade',
        description: 'Open shade on a stone street. Warm, soft, no sun. The street-style default.',
        prompt: 'soft open shade on a city street with no direct sun, warm neutral daylight bounced off pale stone and shopfront glass, a gentle wrap of light on the face, shallow depth of field with the street falling softly out of focus behind',
    },
];
/**
 * Scenes — studio sweeps and real locations.
 *
 * `groundSurface` is what a seated or reclining pose actually rests on, and
 * `suggestedLightingId` is the light that belongs to the place, so choosing a
 * coastal headland does not leave a softbox lighting it.
 */
export const BACKDROP_PRESETS = [
    /* ------------------------------ studio ------------------------------- */
    {
        id: 'seamless-brown',
        name: 'Seamless brown',
        kind: 'studio',
        prompt: 'a seamless brown studio background',
        swatch: '#6B5344',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'seamless-white',
        name: 'Seamless white',
        kind: 'studio',
        prompt: 'a seamless pure white studio background',
        swatch: '#F4F4F2',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'seamless-grey',
        name: 'Seamless grey',
        kind: 'studio',
        prompt: 'a seamless neutral mid-grey studio background',
        swatch: '#8C8C8C',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'seamless-bone',
        name: 'Bone',
        kind: 'studio',
        prompt: 'a seamless warm bone off-white studio background',
        swatch: '#E4DED2',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'seamless-black',
        name: 'Seamless black',
        kind: 'studio',
        prompt: 'a seamless deep black studio background',
        swatch: '#141414',
        groundSurface: 'the studio floor',
    },
    {
        id: 'seamless-sand',
        name: 'Sand',
        kind: 'studio',
        prompt: 'a seamless warm sand-toned studio background',
        swatch: '#C6AD8F',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'seamless-sage',
        name: 'Sage',
        kind: 'studio',
        prompt: 'a seamless muted sage green studio background',
        swatch: '#9AA88F',
        groundSurface: 'the seamless paper floor',
    },
    {
        id: 'concrete',
        name: 'Concrete wall',
        kind: 'studio',
        prompt: 'a smooth pale concrete studio wall',
        swatch: '#B8B5AE',
        groundSurface: 'the polished concrete floor',
    },
    {
        id: 'paper-cyc',
        name: 'Cyclorama',
        kind: 'studio',
        prompt: 'a white paper cyclorama with a soft floor-to-wall curve',
        swatch: '#EDEDEA',
        groundSurface: 'the curved paper floor',
    },
    /* ----------------------------- locations ----------------------------- */
    {
        id: 'moorland-field',
        name: 'Open field',
        kind: 'location',
        prompt: 'an open grass field running flat to the horizon under a wide pale sky, a weathered stone marker standing far off in the distance and a low outcrop of pale lichen-covered rock just behind her',
        swatch: '#8C9478',
        groundSurface: 'the long field grass',
        suggestedLightingId: 'overcast-soft',
    },
    {
        id: 'black-sand-beach',
        name: 'Black sand & mountain',
        kind: 'location',
        prompt: 'a wide black volcanic sand beach with a dark jagged mountain rising steeply behind her under a bruised grey-blue sky, tufts of bleached dry marram grass crossing the near foreground and a pale strip of shallow water along the far shoreline',
        swatch: '#4A4B52',
        groundSurface: 'the wet black sand',
        suggestedLightingId: 'broken-cloud',
    },
    {
        id: 'dune-grass',
        name: 'Dunes & surf',
        kind: 'location',
        prompt: 'a low beach dune thick with pale dry marram grass, the grey open sea breaking behind her and a long breakwater on the far horizon under a flat white sky, the grass and her hair moving in the onshore wind',
        swatch: '#A79B7E',
        groundSurface: 'the dry dune grass',
        suggestedLightingId: 'coastal-grey',
    },
    {
        id: 'boulder-field',
        name: 'Boulders & pasture',
        kind: 'location',
        prompt: 'a green summer pasture with a large rounded limestone boulder crusted in pale grey-white lichen directly behind her, the grass running flat to a far horizon under a high pale sky',
        swatch: '#94A382',
        groundSurface: 'the pasture grass',
        suggestedLightingId: 'overcast-soft',
    },
    {
        id: 'coastal-cliff',
        name: 'Coastal headland',
        kind: 'location',
        prompt: 'a grassy coastal headland with the flat grey sea and dark broken rocks falling away behind her',
        swatch: '#7E8B8A',
        groundSurface: 'the cropped clifftop grass',
        suggestedLightingId: 'coastal-grey',
    },
    {
        id: 'highland-loch',
        name: 'Moor & loch',
        kind: 'location',
        prompt: 'rolling moorland of tussocky grass and wildflowers with a still loch and low green hills behind under a heavy cloudy sky',
        swatch: '#93A183',
        groundSurface: 'the deep meadow grass',
        suggestedLightingId: 'overcast-soft',
    },
    {
        id: 'desert-scrub',
        name: 'Desert scrub',
        kind: 'location',
        prompt: 'open high desert of pale dry earth and sparse grey brush, distant rock formations low on the horizon under a bleached sky',
        swatch: '#B8A183',
        groundSurface: 'the dry sand and gravel',
        suggestedLightingId: 'desert-sun',
    },
    {
        id: 'rural-fence',
        name: 'Field & fence',
        kind: 'location',
        prompt: 'a weathered grey wooden fence at the edge of a damp green field, tall grasses and out-of-focus trees behind',
        swatch: '#75805F',
        groundSurface: 'the wet grass',
        suggestedLightingId: 'overcast-soft',
    },
    {
        id: 'wildflower-meadow',
        name: 'Meadow',
        kind: 'location',
        prompt: 'a high summer meadow of tall seeding grasses scattered with small white and yellow wildflowers',
        swatch: '#A8AE7C',
        groundSurface: 'the tall meadow grass',
        suggestedLightingId: 'golden-hour',
    },
    {
        id: 'forest-edge',
        name: 'Forest edge',
        kind: 'location',
        prompt: 'the edge of a birch wood with pale trunks and soft green shade falling away behind her',
        swatch: '#6E7A5C',
        groundSurface: 'the leaf litter and moss',
        suggestedLightingId: 'overcast-soft',
    },
    {
        id: 'stone-street',
        name: 'Old street',
        kind: 'location',
        prompt: 'a quiet old European street of pale stone buildings and shuttered windows thrown well out of focus behind her',
        swatch: '#B0A18C',
        groundSurface: 'the worn stone paving',
        suggestedLightingId: 'window-daylight',
    },
    {
        id: 'concrete-plaza',
        name: 'Concrete plaza',
        kind: 'location',
        prompt: 'a bare modern concrete plaza with clean geometric planes and a hard shadow line across the ground',
        swatch: '#9C9A95',
        groundSurface: 'the concrete slab',
        suggestedLightingId: 'desert-sun',
    },
    /* ------------------------------- paris -------------------------------- */
    /*
     * One city, several corners of it. Paris is asked for by name more than
     * any other place, and "a Parisian street" on its own gets a postcard:
     * the Eiffel Tower in every frame. Each of these names what is actually
     * in shot — the stone, the zinc, the rattan, the door — so the tower
     * only appears where it belongs, far off on a bridge.
     */
    {
        id: 'paris-street',
        name: 'Parisian street',
        kind: 'location',
        theme: 'paris',
        prompt: 'a quiet Parisian street lined with cream Haussmann limestone façades, wrought-iron balconies and tall shuttered windows, a grey zinc roofline against a pale sky, a narrow pavement with a green cast-iron street lamp and a parked scooter, the street falling away softly out of focus behind',
        swatch: '#B9AE9C',
        groundSurface: 'the worn pavement stone',
        suggestedLightingId: 'street-shade',
    },
    {
        id: 'paris-cafe-terrace',
        name: 'Café terrace',
        kind: 'location',
        theme: 'paris',
        prompt: 'the terrace of a Parisian café, rows of woven rattan bistro chairs in cream and bottle green facing the street, small round marble-topped tables with a tiny espresso cup, a red awning overhead with the menu chalked on a board, the boulevard blurred beyond',
        swatch: '#8E7A5E',
        groundSurface: 'the terrace paving',
        suggestedLightingId: 'window-daylight',
    },
    {
        id: 'paris-cafe-interior',
        name: 'Café interior',
        kind: 'location',
        theme: 'paris',
        prompt: 'the inside of an old Parisian café, a long zinc bar counter with a brass rail, tall mirrors in dark wood frames, bentwood chairs and small marble tables, warm pendant lamps and daylight coming in through the front windows',
        swatch: '#6E5744',
        groundSurface: 'the black and white tiled floor',
        suggestedLightingId: 'window-daylight',
    },
    {
        id: 'paris-bar',
        name: 'Parisian bar, evening',
        kind: 'location',
        theme: 'paris',
        prompt: 'a small Parisian bar at night, a polished zinc counter with bottles lit from behind on mirrored shelves, a red neon sign glowing in the window, dark wood panelling and a few low stools, the street outside a wet blur of lights',
        swatch: '#3A2A2C',
        groundSurface: 'the dark wooden floor',
        suggestedLightingId: 'editorial-contrast',
    },
    {
        id: 'paris-doorway',
        name: 'Haussmann doorway',
        kind: 'location',
        theme: 'paris',
        prompt: 'the entrance of a Haussmann building, a tall heavy carriage door in dark green painted wood with brass fittings set into carved pale limestone, a stone step in front, the building number on an enamel plaque beside the door and a wrought-iron balcony just above',
        swatch: '#A79C88',
        groundSurface: 'the stone doorstep',
        suggestedLightingId: 'street-shade',
    },
    {
        id: 'paris-quay',
        name: 'Seine quay',
        kind: 'location',
        theme: 'paris',
        prompt: 'a cobbled quay along the Seine, the river wide and grey-green beside her, a stone embankment wall with iron mooring rings, a row of plane trees and the green boxes of the bouquinistes on the upper quay, the arches of a stone bridge in the distance',
        swatch: '#7F8A82',
        groundSurface: 'the cobbles of the quay',
        suggestedLightingId: 'street-shade',
    },
    {
        id: 'paris-metro',
        name: 'Métro entrance',
        kind: 'location',
        theme: 'paris',
        prompt: 'the top of a Métro staircase under an Art Nouveau Guimard entrance, sinuous green cast-iron stems holding a red METROPOLITAIN sign and two amber lamps, the iron railing curving down to the steps, a wide Parisian pavement and shopfronts behind',
        swatch: '#5C6B55',
        groundSurface: 'the pavement at the top of the stairs',
        suggestedLightingId: 'street-shade',
    },
    {
        id: 'paris-passage',
        name: 'Covered passage',
        kind: 'location',
        theme: 'paris',
        prompt: 'a nineteenth-century covered passage in Paris, a long glass roof on iron ribs letting in soft light, a mosaic tiled floor, old bookshop and antique shop fronts in dark wood and brass down each side, a few globe lamps on brackets',
        swatch: '#9C8B72',
        groundSurface: 'the mosaic tiles',
        suggestedLightingId: 'window-daylight',
    },
    {
        id: 'paris-montmartre',
        name: 'Montmartre steps',
        kind: 'location',
        theme: 'paris',
        prompt: 'a steep flight of stone steps in Montmartre with a double iron handrail down the middle and old lamp posts on each landing, ivy on the walls either side, the white domes of the Sacré-Cœur just visible at the top against the sky',
        swatch: '#8A8F7C',
        groundSurface: 'the stone steps',
        suggestedLightingId: 'golden-hour',
    },
    {
        id: 'paris-bridge',
        name: 'Bridge over the Seine',
        kind: 'location',
        theme: 'paris',
        prompt: 'the middle of a stone bridge over the Seine, ornate iron lamp posts along the parapet, the river and its embankments stretching away, the Eiffel Tower small and hazy in the far distance, a soft late sky',
        swatch: '#9AA3A6',
        groundSurface: 'the bridge pavement',
        suggestedLightingId: 'golden-hour',
    },
    {
        id: 'paris-kerb',
        name: 'At the crossing',
        kind: 'location',
        theme: 'paris',
        prompt: 'the kerb of a Paris crossing, a black and cream pedestrian light post with its red figure lit just beside her, a tall gilded wrought-iron railing with gold-tipped spikes behind, a carved stone lion head set into the pale limestone façade beyond it, a few bicycles chained to the railing and the street softly out of focus',
        swatch: '#8B8468',
        groundSurface: 'the pavement at the kerb',
        suggestedLightingId: 'street-shade',
    },
];
/**
 * Colour grade — how the film was developed, as distinct from how the set was
 * lit. This is what separates a clean e-commerce cutout from the muted,
 * grainy, medium-format look of a campaign image.
 */
export const GRADE_PRESETS = [
    {
        id: 'clean-digital',
        name: 'Clean digital',
        description: 'True-to-life colour. For e-commerce.',
        prompt: 'clean neutral digital colour, accurate skin tones, no grain, no colour cast',
    },
    {
        id: 'muted-editorial',
        name: 'Muted editorial',
        description: 'Desaturated, soft contrast, fine grain. Campaign look.',
        prompt: 'muted desaturated editorial colour grade with soft rolled-off contrast, slightly lifted blacks, fine natural film grain and the shallow tonality of a medium-format negative',
    },
    {
        id: 'warm-film',
        name: 'Warm film',
        description: 'Analogue warmth, gentle halation, visible grain.',
        prompt: 'warm analogue film grade with golden midtones, gentle halation around the highlights and visible 35mm grain',
    },
    {
        id: 'cool-nordic',
        name: 'Cool Nordic',
        description: 'Cold, grey-green, very low saturation.',
        prompt: 'cool desaturated Nordic grade with grey-green shadows, very low saturation, flat soft contrast and a faint cold cast across the whites',
    },
    {
        id: 'rich-contrast',
        name: 'Rich contrast',
        description: 'Deep blacks, saturated colour, punchy.',
        prompt: 'rich saturated colour with deep blacks and crisp micro-contrast, punchy and glossy like a printed cover image',
    },
    {
        id: 'black-white',
        name: 'Black & white',
        description: 'Monochrome, full tonal range.',
        prompt: 'black and white photograph with a full tonal range from deep black to clean white, rich mid-grey separation and fine grain',
    },
];
export function findGrade(id) {
    return GRADE_PRESETS.find((g) => g.id === id);
}
/* ------------------------------------------------------------------ *
 * Film stocks
 * ------------------------------------------------------------------ *
 *
 * Named for what they do rather than after any manufacturer's product. The
 * numbers are the development pass; the prompt is what the renderer can do
 * that a post-process cannot — halation in the optics, contrast baked into
 * the exposure, the way a stock renders skin.
 */
export const NO_STOCK_ID = 'digital';
export const FILM_STOCKS = [
    {
        id: NO_STOCK_ID,
        name: 'Digital',
        description: 'Clean. No development pass runs at all.',
        prompt: '',
        grain: 0,
        clump: 1,
        lift: 0,
        warmth: 0,
        saturation: 1,
        halation: 0,
    },
    {
        id: 'fine-400',
        name: 'Fine grain 400',
        description: 'Modern colour negative. Grain you feel more than see.',
        prompt: 'shot on 400-speed colour negative film: gentle contrast, creamy highlight roll-off and natural skin rendition',
        grain: 26,
        clump: 1.6,
        lift: 0.04,
        warmth: 0.12,
        saturation: 0.97,
        halation: 0.12,
    },
    {
        id: 'pushed-800',
        name: 'Pushed 800',
        description: 'Grain you can read. Cooler shadows, softer blacks.',
        prompt: 'shot on 800-speed colour negative pushed one stop: open shadows, softened blacks and a coarse honest grain structure',
        grain: 46,
        clump: 2.3,
        lift: 0.09,
        warmth: 0.04,
        saturation: 0.92,
        halation: 0.18,
    },
    {
        id: 'pushed-1600',
        name: 'Pushed 1600',
        description: 'Heavy grain, flat blacks. Late-night editorial.',
        prompt: 'shot on 1600-speed film pushed hard: heavy grain, flat milky blacks, low saturation and blown speculars',
        grain: 72,
        clump: 3.1,
        lift: 0.16,
        warmth: 0.02,
        saturation: 0.84,
        halation: 0.26,
    },
    {
        id: 'faded-70s',
        name: 'Faded 70s',
        description: 'Warm cast, milky blacks, halation. The vintage one.',
        prompt: 'a faded 1970s colour photograph: warm amber cast, milky lifted blacks, low saturation, soft halation blooming out of the highlights and the slightly soft focus of an older lens',
        grain: 58,
        clump: 2.7,
        lift: 0.22,
        warmth: 0.55,
        saturation: 0.78,
        halation: 0.42,
    },
    {
        id: 'mono-400',
        name: 'Monochrome 400',
        description: 'Black and white, full tonal range, real silver grain.',
        prompt: 'black and white 400-speed film with a full tonal range from deep black to clean white and rich mid-grey separation',
        grain: 52,
        clump: 2.0,
        lift: 0.05,
        warmth: 0,
        saturation: 0,
        halation: 0.1,
    },
    {
        id: 'slide-100',
        name: 'Slide 100',
        description: 'Very fine grain, deep blacks, saturated colour.',
        prompt: 'shot on 100-speed colour transparency film: dense blacks, high saturation, crisp micro-contrast and almost invisible grain',
        grain: 14,
        clump: 1.2,
        lift: 0,
        warmth: 0.06,
        saturation: 1.12,
        halation: 0.06,
    },
];
export function findStock(id) {
    return FILM_STOCKS.find((f) => f.id === id);
}
/** The stock a shoot was set to, falling back to no development at all. */
export function resolveStock(id) {
    return findStock(id ?? NO_STOCK_ID) ?? FILM_STOCKS[0];
}
/* ------------------------------------------------------------------ *
 * Apertures
 * ------------------------------------------------------------------ */
export const AUTO_APERTURE_ID = 'auto';
export const APERTURES = [
    { id: AUTO_APERTURE_ID, name: 'Auto', description: 'What the place calls for.', prompt: '' },
    {
        id: 'f1.4',
        name: 'f/1.4',
        description: 'Only her in focus; the place melts away.',
        prompt: 'shot wide open at f/1.4: a very shallow depth of field — only she is in sharp focus, the place behind her dissolves into soft blur with round bokeh',
    },
    {
        id: 'f2',
        name: 'f/2',
        description: 'Her sharp, the place clearly soft.',
        prompt: 'shot at f/2: a shallow depth of field — she is sharp, the place behind her clearly soft',
    },
    {
        id: 'f2.8',
        name: 'f/2.8',
        description: 'The place gently out of focus, still readable.',
        prompt: 'shot at f/2.8: she is sharp, the place behind her gently out of focus but still readable',
    },
    {
        id: 'f4',
        name: 'f/4',
        description: 'The place only slightly soft.',
        prompt: 'shot at f/4: she is sharp and the place behind her only slightly soft, its details readable',
    },
    {
        id: 'f5.6',
        name: 'f/5.6',
        description: 'Most of the place sharp.',
        prompt: 'shot at f/5.6: she and most of the place are sharp, only the far background barely soft',
    },
    {
        id: 'f8',
        name: 'f/8',
        description: 'Deep focus: her and the place sharp.',
        prompt: 'shot at f/8: deep focus — she and the place are sharp from front to back',
    },
    {
        id: 'f11',
        name: 'f/11',
        description: 'Everything sharp, near to far.',
        prompt: 'shot at f/11: everything is sharp, from the foreground to the far background',
    },
];
export function findAperture(id) {
    return APERTURES.find((a) => a.id === id);
}
export const ASPECT_RATIOS = ['2:3', '3:4', '4:5', '1:1', '4:3', '16:9', '9:16'];
export function findPov(id) {
    return CAMERA_POVS.find((p) => p.id === id);
}
export function findLighting(id) {
    return LIGHTING_PRESETS.find((p) => p.id === id);
}
export const CUSTOM_SCENE_ID = 'custom';
export function findBackdrop(id) {
    return BACKDROP_PRESETS.find((p) => p.id === id);
}
/**
 * The built-in places the studio offers: the plain backgrounds and four
 * streets. The others stay in `BACKDROP_PRESETS`, so a roll shot in one of
 * them still reads and reshoots as it was, but none is offered any more.
 */
export const OFFERED_SCENE_IDS = [
    'seamless-brown',
    'seamless-white',
    'seamless-grey',
    'seamless-bone',
    'seamless-black',
    'seamless-sand',
    'seamless-sage',
    'stone-street',
    'paris-street',
    'paris-doorway',
    'paris-kerb',
];
export const OFFERED_BACKDROPS = OFFERED_SCENE_IDS.map((id) => findBackdrop(id)).filter((b) => Boolean(b));
/**
 * The scene to compose with. Resolves the `custom` id against whatever the
 * user typed, so the composer never has to special-case it.
 */
export function resolveScene(backdropId, customBackdrop = '') {
    if (backdropId !== CUSTOM_SCENE_ID)
        return findBackdrop(backdropId);
    const text = customBackdrop.trim();
    if (!text)
        return undefined;
    return {
        id: CUSTOM_SCENE_ID,
        name: 'Custom',
        kind: 'location',
        prompt: text,
        swatch: '#3d372f',
        groundSurface: 'the ground',
    };
}
/* ------------------------------------------------------------------ *
 * Accessories
 * ------------------------------------------------------------------ */
/**
 * Accessories the stylist can add to a look without a photograph. Each is
 * a noun phrase the stylist writes into the outfit; the render has no
 * reference for it, so it is named the way a credit line would name it.
 */
export const ACCESSORY_PRESETS = [
    { id: 'sunglasses', name: 'Sunglasses', prompt: 'a pair of dark-lensed sunglasses' },
    {
        id: 'slim-sunglasses',
        name: 'Slim sunglasses',
        prompt: 'a pair of narrow rectangular black sunglasses with slim arms',
    },
    { id: 'glasses', name: 'Glasses', prompt: 'a pair of thin-rimmed optical glasses' },
    { id: 'necklace', name: 'Necklace', prompt: 'a fine gold chain necklace' },
    { id: 'earrings', name: 'Earrings', prompt: 'a pair of small gold hoop earrings' },
    { id: 'ring', name: 'Ring', prompt: 'a slim gold ring' },
    { id: 'bracelet', name: 'Bracelet', prompt: 'a thin gold bracelet' },
    { id: 'bangles', name: 'Gold bangles', prompt: 'a stack of three or four gold bangles on one wrist' },
    { id: 'watch', name: 'Watch', prompt: 'a slim steel wristwatch on a leather strap' },
    { id: 'belt', name: 'Belt', prompt: 'a narrow leather belt with a small metal buckle' },
    { id: 'scarf', name: 'Scarf', prompt: 'a lightweight silk scarf knotted at the neck' },
    { id: 'cap', name: 'Cap', prompt: 'a plain cotton baseball cap' },
    { id: 'beanie', name: 'Beanie', prompt: 'a ribbed knit beanie' },
    { id: 'hat', name: 'Wide-brim hat', prompt: 'a wide-brimmed felt hat' },
    { id: 'handbag', name: 'Handbag', prompt: 'a small structured leather handbag carried in the hand' },
    { id: 'tote', name: 'Tote bag', prompt: 'a canvas tote bag over the shoulder' },
    { id: 'crossbody', name: 'Crossbody bag', prompt: 'a small leather crossbody bag on a long strap' },
];
export function findAccessory(id) {
    return ACCESSORY_PRESETS.find((a) => a.id === id);
}
/* ------------------------------------------------------------------ *
 * Expression
 * ------------------------------------------------------------------ */
/**
 * How much the face is smiling, from none to a laugh.
 *
 * Kept apart from the pose. Most poses carry an expression of their own —
 * "a calm neutral expression", "an unsmiling look" — and that stays the
 * default (`auto`). Set to anything else it is written as its own line and
 * told to win over whatever the pose said, so a catalogue pose can be shot
 * smiling without rewriting the pose.
 */
export const EXPRESSIONS = [
    { id: 'auto', name: 'As the pose', prompt: '' },
    { id: 'neutral', name: 'No smile', prompt: 'a composed neutral expression, lips closed, no smile' },
    {
        id: 'hint',
        name: 'Hint of a smile',
        prompt: 'the faintest hint of a smile at the corners of the mouth, almost imperceptible',
    },
    { id: 'soft', name: 'Soft smile', prompt: 'a soft closed-lip smile, relaxed and warm' },
    { id: 'open', name: 'Open smile', prompt: 'a natural open smile showing teeth, the eyes engaged with it' },
    { id: 'laugh', name: 'Laughing', prompt: 'a broad genuine laugh, head tipped back a little, eyes creased' },
    {
        id: 'talking',
        name: 'Mid-sentence',
        prompt: 'lips parted mid-sentence as if answering someone just off camera, the eyes lively and engaged',
    },
];
export function findExpression(id) {
    return EXPRESSIONS.find((e) => e.id === (id ?? 'auto'));
}
/**
 * A scene left to the stylist. Not a preset — the stylist picks one of the
 * real presets for the look, and the roll records which, so `resolveScene`
 * never sees this id once the roll is running.
 */
export const AUTO_SCENE_ID = 'auto';
/* ------------------------------------------------------------------ *
 * House recipes
 * ------------------------------------------------------------------ */
const STREET_BASE = {
    aspectRatio: '4:5',
    lightingId: 'street-shade',
    backdropId: 'paris-street',
    gradeId: 'warm-film',
    filmStockId: NO_STOCK_ID,
    grain: 0,
    customBackdrop: '',
    poseIds: [],
    variantsPerPov: 1,
    details: '',
    styleNotes: 'candid street-style editorial, caught rather than posed, warm earthy palette, shallow depth of field',
    qualityThreshold: 70,
    maxRetriesPerShot: 1,
    retryMode: 'auto',
    copySpace: 'none',
    useGarmentReferences: true,
    useModelReferences: true,
    expression: 'hint',
    accessories: [],
    accessoryNotes: '',
    stylingMode: 'complete',
};
/**
 * Looks the studio ships with, built from the kind of photograph a Paris
 * street-style account posts: a textured statement piece over a plain
 * shirt, high-waisted trousers, gold hoops and bangles, slim sunglasses, a
 * small structured bag, limestone and gilded iron behind, no sun. They are
 * applied with one press and then edited like anything else; the cast is
 * always yours.
 */
export const HOUSE_RECIPES = [
    {
        id: 'paris-street-style',
        name: 'Paris street style',
        description: 'Walking the street and waiting at the crossing, three poses, warm film.',
        povIds: ['full-three-quarter', 'three-quarter-crop', 'bust-portrait'],
        settings: {
            ...STREET_BASE,
            poseIds: ['stride-pocket', 'kerb-bag', 'coffee-walk'],
            variantsPerPov: 3,
            accessories: ['slim-sunglasses', 'earrings', 'bangles', 'ring', 'handbag'],
        },
    },
    {
        id: 'paris-cafe',
        name: 'Café parisien',
        description: 'Seated inside and on the terrace, mid-conversation, window light.',
        povIds: ['three-quarter-crop', 'bust-portrait'],
        settings: {
            ...STREET_BASE,
            backdropId: 'paris-cafe-interior',
            lightingId: 'window-daylight',
            poseIds: ['cafe-menu', 'cafe-table'],
            variantsPerPov: 2,
            expression: 'talking',
            accessories: ['earrings', 'bangles', 'ring'],
        },
    },
    {
        id: 'paris-crossing',
        name: 'Au carrefour',
        description: 'At the kerb by the gilded railing, full length, sunglasses on.',
        povIds: ['full-front', 'full-three-quarter'],
        settings: {
            ...STREET_BASE,
            backdropId: 'paris-kerb',
            poseIds: ['kerb-bag', 'stride-pocket'],
            variantsPerPov: 2,
            expression: 'neutral',
            accessories: ['slim-sunglasses', 'earrings', 'bangles', 'crossbody'],
        },
    },
    {
        id: 'paris-influencer',
        name: 'Instagrameuse',
        description: 'Influencer poses on a Paris street, relaxed and smiling, outfit of the day.',
        povIds: ['full-front', 'three-quarter-crop'],
        settings: {
            ...STREET_BASE,
            poseIds: ['cross-step', 'sunglasses-lower', 'look-back-smile'],
            variantsPerPov: 3,
            expression: 'soft',
            attitude: 'influencer',
            accessories: ['sunglasses', 'earrings', 'crossbody'],
        },
    },
];
export function findHouseRecipe(id) {
    return HOUSE_RECIPES.find((r) => r.id === id);
}
/* ------------------------------------------------------------------ *
 * Attitude
 * ------------------------------------------------------------------ */
/**
 * How the model carries herself, and how dressed-up the look is. The same
 * vest reads as a catalogue page or as a post from someone's day depending
 * on this more than on anything in the camera line.
 */
export const ATTITUDES = [
    { id: 'editorial', name: 'Editorial', prompt: '', styling: '' },
    {
        id: 'influencer',
        name: 'Influencer',
        prompt: 'the easy, knowing energy of an Instagram fashion influencer photographed by a friend — candid and approachable, a little playful, natural movement through the body, nothing stiff or catalogue-posed',
        styling: 'Dress it like a fashion influencer’s outfit of the day: on-trend but wearable, one statement piece, sneakers or simple flats are fine, accessories that photograph well on a phone. Less formal than an editorial.',
    },
    {
        id: 'relaxed',
        name: 'Relaxed',
        prompt: 'a relaxed, unpretentious, everyday attitude — loose shoulders, easy weight, a natural face, nothing haughty or high-fashion about it',
        styling: 'Dress it down: casual and comfortable rather than chic — denim, a plain tee or knit, clean sneakers or loafers, minimal jewellery. Nothing that looks like it needed a stylist.',
    },
];
export function findAttitude(id) {
    return ATTITUDES.find((a) => a.id === (id ?? 'editorial'));
}
/* ------------------------------------------------------------------ *
 * Garment length
 * ------------------------------------------------------------------ */
/** Where a hem falls, in the words a renderer reads. */
export const LENGTH_PHRASES = {
    'not-applicable': '',
    'cropped-above-waist': 'cropped, ending above the natural waist',
    waist: 'ending at the natural waist',
    'high-hip': 'ending at the high hip, just below the waist',
    hip: 'ending at the hip bone',
    'low-hip': 'ending at the low hip, covering the seat',
    'upper-thigh': 'ending at the upper thigh',
    'mid-thigh': 'ending at mid-thigh',
    knee: 'ending at the knee',
    'below-knee': 'ending just below the knee',
    'mid-calf': 'ending at mid-calf',
    ankle: 'ending at the ankle',
    floor: 'reaching the floor',
};
/** Where a sleeve ends, in the words a renderer reads. */
export const SLEEVE_PHRASES = {
    'not-applicable': '',
    sleeveless: 'sleeveless — bare shoulders and arms',
    cap: 'cap sleeves just covering the shoulder',
    short: 'short sleeves ending at the upper arm',
    elbow: 'sleeves ending at the elbow',
    'three-quarter': 'three-quarter sleeves ending between elbow and wrist',
    full: 'full-length sleeves reaching the wrist',
    'extra-long': 'extra-long sleeves falling past the wrist over the hand',
};
/* ------------------------------------------------------------------ *
 * The preset composer's short lists
 * ------------------------------------------------------------------ */
/**
 * The camera angles the preset composer offers. The others stay valid — an
 * old roll or a saved recipe that uses them still shoots — but a first-time
 * choice between fifteen near-neighbours is no choice at all.
 */
export const CORE_POV_IDS = [
    'full-front',
    'full-three-quarter',
    'full-profile',
    'full-back',
    'three-quarter-crop',
    'bust-portrait',
    'walking',
    'detail-fabric',
];
/**
 * The poses the preset composer offers, in the three families a shoot is
 * planned in: fashion shows the clothes move and fit, commercial is
 * relatable and uses the product, editorial tells a story or holds a mood.
 * Every other pose stays valid for rolls that already use it.
 */
export const POSE_CATEGORIES = {
    fashion: [
        'mid-stride',
        'walk-away-glance',
        'spin-turn',
        'cross-step',
        'knee-pop',
        'leg-cross-stand',
        'weight-shift',
        'hands-pockets',
        'hand-hip',
        'coat-open',
        'jacket-wrap',
    ],
    commercial: [
        'holding-bag',
        'product-present',
        'hat-brim',
        'coffee-walk',
        'laughing-off-frame',
        'look-back-smile',
        'hands-on-hips',
        'hand-on-face',
        'hands-in-hair',
        'cafe-table',
        'leaning-wall',
        'seated-stool',
    ],
    editorial: [
        'stare-down',
        'eyes-closed',
        'hand-on-head',
        'angular-editorial',
        'shadow-turn',
        'looking-away',
        'wall-shoulder',
        'sit-forward',
        'seated-ground',
        'crouching',
    ],
};
/** Which family a pose is offered in, or undefined for one kept only for old rolls. */
export function poseCategoryOf(poseId) {
    return Object.keys(POSE_CATEGORIES).find((c) => POSE_CATEGORIES[c].includes(poseId));
}
//# sourceMappingURL=presets.js.map